import {
  SurveyDraft,
  SurveyQuestion,
  SurveyQuestionIf,
  SurveyQuestionIfType,
} from '../types/Survey.type';
import { ChiroUpJSON } from './ChiroUpJSON';

const hasUsableConditionValue = (condition: SurveyQuestionIf): boolean => {
  if (condition.type === SurveyQuestionIfType.notAnswered) return true;
  if (Array.isArray(condition.value)) return condition.value.length > 0;

  return condition.value !== undefined && condition.value !== null && condition.value !== '';
};

const sanitizeConditions = (
  conditions: SurveyQuestionIf[] | undefined
): SurveyQuestionIf[] | undefined => {
  const usable = (conditions || []).filter(
    (condition): condition is SurveyQuestionIf => !!condition && hasUsableConditionValue(condition)
  );

  return usable.length ? usable.map(condition => ({ ...condition })) : undefined;
};

const sanitizeQuestion = (question: SurveyQuestion, stripAnswer: boolean): SurveyQuestion => {
  const sanitized: SurveyQuestion = { ...question };

  if (stripAnswer) {
    delete sanitized.answer;
    delete sanitized.answeredAt;
  }

  const conditions = sanitizeConditions(question.if);
  const someConditions = sanitizeConditions(question.ifSome);

  if (conditions) sanitized.if = conditions;
  else delete sanitized.if;

  if (someConditions) sanitized.ifSome = someConditions;
  else delete sanitized.ifSome;

  if (question.typeOptions?.subQuestions) {
    sanitized.typeOptions = {
      ...question.typeOptions,
      subQuestions: question.typeOptions.subQuestions
        .filter((subQuestion): subQuestion is SurveyQuestion => !!subQuestion)
        .map(subQuestion => sanitizeQuestion(subQuestion, stripAnswer)),
    };
  }

  return sanitized;
};

/**
 * A survey template defines the initial UI state. Patient progress belongs in
 * PatientSurvey answers/drafts, never in the reusable template definition.
 */
export const sanitizeSurveyTemplateQuestions = (
  questions: SurveyQuestion[] | undefined
): SurveyQuestion[] =>
  (questions || [])
    .filter((question): question is SurveyQuestion => !!question)
    .map(question => sanitizeQuestion(question, true));

const getTemplateQuestionId = (
  question: SurveyQuestion,
  templateQuestionIds: Set<string>
): string | undefined => {
  if (templateQuestionIds.has(question.id)) return question.id;

  if (question.complaintId) {
    const complaintSuffix = `-${question.complaintId}`;

    if (question.id.endsWith(complaintSuffix)) {
      const templateId = question.id.slice(0, -complaintSuffix.length);
      if (templateQuestionIds.has(templateId)) return templateId;
    }
  }

  return Array.from(templateQuestionIds).find(templateId =>
    question.id.startsWith(`${templateId}-`)
  );
};

/**
 * Repairs incomplete re-evaluation snapshots created from a polluted template.
 * A matching draft answer is inherited initialization, while a different draft
 * answer represents an explicit patient selection and must be preserved.
 */
export const normalizeIncompleteReevaluationSurvey = ({
  questions,
  draft,
}: {
  questions: SurveyQuestion[] | undefined;
  draft?: SurveyDraft | null;
}): { questions: SurveyQuestion[]; draft?: SurveyDraft | null } => {
  const templateQuestions = (questions || []).filter(
    (question): question is SurveyQuestion => !!question
  );
  const templateQuestionsById = new Map(
    templateQuestions.map(question => [question.id, question] as const)
  );
  const templateQuestionIds = new Set(templateQuestionsById.keys());

  const normalizeDraftQuestions = (draftQuestions: SurveyQuestion[] | undefined) =>
    (draftQuestions || [])
      .filter((question): question is SurveyQuestion => !!question)
      .map(question => {
        const templateId = getTemplateQuestionId(question, templateQuestionIds);
        const templateAnswer = templateId
          ? templateQuestionsById.get(templateId)?.answer
          : undefined;
        const inheritedTemplateAnswer =
          templateAnswer != null &&
          question.answer != null &&
          question.answeredAt == null &&
          ChiroUpJSON.deepEqualAnyOrder(templateAnswer, question.answer);

        return sanitizeQuestion(question, inheritedTemplateAnswer);
      });

  return {
    questions: sanitizeSurveyTemplateQuestions(templateQuestions),
    draft: draft
      ? {
          ...draft,
          questions: normalizeDraftQuestions(draft.questions),
          ...(draft.spanishQuestions !== undefined
            ? { spanishQuestions: normalizeDraftQuestions(draft.spanishQuestions) }
            : {}),
        }
      : draft,
  };
};
